import { Box, Stack, Typography, useTheme, Tooltip } from "@mui/material";
import { useCallback, useState, useEffect } from "react";
import SideNavItem from "./SideNavItem";
import { Item, permissionCheck } from "layouts/Config";
import AddIcon from "@mui/icons-material/Add";

interface CustomProps {
  items?: Item[];
  pathname?: string | null;
  subheader?: string;
  isCollapsed: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const renderItems = ({
  depth = 0,
  items,
  pathname,
  isCollapsed,
  openItems,
  onToggle,
  onItemClick,
}: any): JSX.Element[] =>
  items.reduce(
    (acc: JSX.Element[], item: any) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
        isCollapsed,
        openItems,
        onToggle,
        onItemClick,
      }),
    []
  );

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
  isCollapsed,
  openItems,
  onToggle,
  onItemClick,
}: any): JSX.Element[] => {
  const checkPath = !!(item.path && pathname);
  const partialMatch = checkPath ? pathname.includes(item.path) : false;
  const exactMatch = checkPath ? pathname === item.path : false;
  const underDevelopment = item.underDevelopment ?? false;
  const hasAccess = permissionCheck(item);

  if (underDevelopment || !hasAccess) return acc;

  const hasChildren = !!item.items;
  const isChildActive = hasChildren && item.items.some((child: any) => {
    return child.path && pathname && (pathname === child.path || pathname.includes(child.path));
  });
  const isOpen = openItems.has(item.title);

  acc.push(
    <SideNavItem
      key={item.title}
      title={item.title}
      path={item.path}
      icon={item.icon}
      activeIcon={item.activeIcon}
      active={exactMatch || partialMatch || (isChildActive && isCollapsed)}
      highlighted={exactMatch || partialMatch || isChildActive}
      isCollapsed={isCollapsed}
      isOpen={isOpen}
      onToggle={() => onToggle(item.title)}
      onItemClick={() => onItemClick(item.title, hasChildren, depth)}
      depth={depth}
      disabled={item.disabled}
      featureKey={item.featureKey}
      beta={item.beta}
    >
      {hasChildren && (
        <Stack
          component="ul"
          spacing="3px"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            mt: "3px",
            ml: isCollapsed ? 0 : 3,
            // The rail the child rows hang off; rows sit inset from it so the
            // active pill clears the line.
            pl: isCollapsed ? 0 : "12px",
            borderLeft: isCollapsed ? "none" : "1px solid var(--nav-border-color)",
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
            isCollapsed,
            openItems,
            onToggle,
            onItemClick,
          })}
        </Stack>
      )}
    </SideNavItem>
  );

  return acc;
};

const SideNavSection = (props: CustomProps) => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const theme = useTheme();

  useEffect(() => {
    if (props.pathname) {
      props.items?.forEach((item) => {
        if (item.items) {
          const hasActiveChild = item.items.some(
            (child) => child.path && props.pathname?.includes(child.path)
          );
          if (hasActiveChild) {
            setOpenItems((prev) => {
              const newSet = new Set(prev);
              newSet.add(item.title);
              return newSet;
            });
          }
        }
      });
    }
  }, [props.pathname, props.items]);

  const handleToggle = useCallback((title: string) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      newSet.has(title) ? newSet.delete(title) : newSet.add(title);
      return newSet;
    });
  }, []);

  const handleItemClick = useCallback(
    (title: string, hasChildren: boolean, depth: number) => {
      if (!hasChildren && depth === 0) {
        setOpenItems(new Set());
      }
    },
    []
  );

  return (
    <Stack
      component="ul"
      spacing="3px"
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        px: props.isCollapsed ? 0 : 2,
        pt: props.action || props.subheader ? 2 : 0,
      }}
    >
      {props.action && (
        <Box
          component="li"
          sx={{
            mb: 1,
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none",
            justifyContent: props.isCollapsed ? "center" : "flex-start",
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label={props.action.label}
            onClick={props.action.onClick}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
              border: "none",
              background: "transparent",
              p: 0,
              m: 0,
              color: "inherit",
              font: "inherit",
              "&:focus-visible": {
                outline: (t) => `2px solid ${t.palette.primary.main}`,
                outlineOffset: "2px",
                borderRadius: "8px",
              },
            }}
          >
            {props.isCollapsed ? (
              <Tooltip title={props.action.label} placement="right" arrow>
                <Box
                  component="span"
                  sx={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--nav-item-active-color)",
                    p: 0,
                    m: 0,
                  }}
                >
                  <AddIcon sx={{ color: "inherit", fontSize: 22 }} />
                </Box>
              </Tooltip>
            ) : (
              <Stack
                component="span"
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 2.5,
                  pb: 1,
                }}
              >
                <AddIcon sx={{ color: "inherit", fontSize: 18 }} />
                <Typography component="span" variant="subtitle1">
                  {props.action.label}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      )}

      {props.subheader &&
        (props.isCollapsed ? (
          // <Box
          //   sx={{
          //     width: "100%",
          //     height: 10,
          //     display: "flex",
          //     alignItems: "center",
          //     justifyContent: "center",
          //     my: 0.2,
          //   }}
          // >
          //   <Box
          //     sx={{
          //       width: 18,
          //       height: 2.5,
          //       borderRadius: 1,
          //       background: "var(--nav-section-title-color)",
          //       opacity: 0.8,
          //     }}
          //   />
          // </Box>
          <></>
        ) : (
          <Typography
            component="li"
            variant="caption"
            sx={{
              textTransform: "uppercase",
              background: "var(--nav-section-title-color)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontWeight: 600,
              letterSpacing: 1,
              fontSize: 13,
              pl: 1,
              mb: 0.5,
            }}
          >
            {props.subheader}
          </Typography>
        ))}

      {renderItems({
        items: props.items ?? [],
        pathname: props.pathname,
        isCollapsed: props.isCollapsed,
        openItems,
        onToggle: handleToggle,
        onItemClick: handleItemClick,
      })}
    </Stack>
  );
};

export default SideNavSection;
